import { useMemo, useEffect } from 'react'
import {
  ArrayInput,
  SimpleFormIterator,
  TextInput,
  NumberInput,
  SelectInput,
  SelectArrayInput,
  FormDataConsumer,
  useSimpleFormIteratorItem,
} from 'react-admin'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  IconButton,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteIcon from '@mui/icons-material/Delete'
import { useFormContext, useWatch } from 'react-hook-form'

import { validateRequired } from '../../utils/validate'
import RichTextInput from '../../components/RichTextInput'

const OrderedCorrectAnswerManager = ({ choices = [] }) => {
  const { index } = useSimpleFormIteratorItem()
  const { setValue } = useFormContext()
  const correctAnswerPath = `quizzes.${index}.correctAnswer`
  const selectedIds = useWatch({ name: correctAnswerPath }) || []

  const choiceMap = useMemo(() => {
    const map = {}
    choices.forEach(c => {
      if (c && c.id !== undefined) map[c.id] = c
    })
    return map
  }, [choices])

  const onReorder = nextOrder => {
    setValue(correctAnswerPath, nextOrder, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const moveUp = idx => {
    if (idx <= 0) return
    const items = Array.from(selectedIds)
    ;[items[idx - 1], items[idx]] = [items[idx], items[idx - 1]]
    onReorder(items)
  }

  const moveDown = idx => {
    if (idx >= selectedIds.length - 1) return
    const items = Array.from(selectedIds)
    ;[items[idx + 1], items[idx]] = [items[idx], items[idx + 1]]
    onReorder(items)
  }

  const handleDragEnd = result => {
    if (!result.destination) return
    const items = Array.from(selectedIds)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)
    onReorder(items)
  }

  if (!selectedIds?.length) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
        Drag to order correct answers
      </Typography>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`ordered-correct-${index}`}>
          {provided => (
            <List
              dense
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                width: '100%',
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid #eee',
                borderRadius: 1,
              }}>
              {selectedIds.map((id, idx) => {
                const ch = choiceMap[id]
                return (
                  <Draggable
                    key={`${index}-${id}`}
                    draggableId={`${index}-${id}`}
                    index={idx}>
                    {draggableProvided => (
                      <ListItem
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => moveUp(idx)}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveDown(idx)}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const next = selectedIds.filter(v => v !== id)
                                onReorder(next)
                              }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }>
                        <Chip
                          label={`#${idx + 1}`}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <ListItemText primary={ch?.name ?? String(id)} />
                      </ListItem>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  )
}

const OrderInput = () => {
  const { index } = useSimpleFormIteratorItem()
  const { setValue } = useFormContext()

  const orderPath = `quizzes.${index}.order`
  const episodePath = `quizzes.${index}.episodeId`

  const currentOrder = useWatch({ name: orderPath })
  const currentEpisodeId = useWatch({ name: episodePath })
  const allQuizzes = useWatch({ name: 'quizzes' }) || []

  useEffect(() => {
    if (currentOrder && currentOrder > 0) return

    if (currentEpisodeId) {
      const quizzesInEpisode = allQuizzes.filter(
        (q, idx) =>
          q && q.episodeId === currentEpisodeId && idx !== index && q.order,
      )

      let nextOrder = 1
      if (quizzesInEpisode.length > 0) {
        const maxOrder = Math.max(...quizzesInEpisode.map(q => q.order || 0))
        nextOrder = maxOrder + 1
      }

      setValue(orderPath, nextOrder, { shouldDirty: true })
    }
  }, [currentEpisodeId, currentOrder, allQuizzes, orderPath, setValue, index])

  useEffect(() => {
    if (!currentOrder || !currentEpisodeId) return

    const hasDuplicate = allQuizzes.some(
      (q, idx) =>
        idx !== index &&
        q &&
        q.episodeId === currentEpisodeId &&
        q.order === currentOrder,
    )

    if (hasDuplicate) {
      const quizzesInEpisode = allQuizzes.filter(
        (q, idx) =>
          q && q.episodeId === currentEpisodeId && idx !== index && q.order,
      )

      const usedOrders = new Set(quizzesInEpisode.map(q => q.order))
      let newOrder = currentOrder + 1

      while (usedOrders.has(newOrder)) {
        newOrder++
      }

      setValue(orderPath, newOrder, { shouldDirty: true })
    }
  }, [currentOrder, currentEpisodeId, allQuizzes, orderPath, setValue, index])

  return (
    <NumberInput
      source="order"
      label="Order"
      fullWidth
      variant="standard"
      min={1}
      helperText="Thứ tự hiển thị của quiz trong episode "
    />
  )
}

const AnswerInput = () => {
  const { index } = useSimpleFormIteratorItem()

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={4} md={1}>
        <NumberInput
          source="id"
          label="ID"
          variant="standard"
          defaultValue={index + 1}
        />
      </Grid>
      <Grid item xs={12} sm={8} md={11}>
        <TextInput source="value" label="Value" fullWidth variant="standard" />
      </Grid>
    </Grid>
  )
}

const CorrectAnswerInput = () => {
  return (
    <FormDataConsumer>
      {({ scopedFormData }) => {
        const allAnswers = scopedFormData?.answers || []
        const answers = allAnswers.filter(
          a => a && (a.value || a.id !== undefined),
        )
        const choices = answers
          .map((a, idx) => {
            const id =
              a.id !== undefined && a.id !== null ? Number(a.id) : idx + 1
            return {
              id,
              name: a.value || '',
            }
          })
          .filter(choice => choice.name && choice.name.trim())
        const selType = scopedFormData?.answerSelectionType

        if (!choices.length) {
          return (
            <Typography variant="body2" color="text.secondary">
              Add answers first to choose correct answer(s).
            </Typography>
          )
        }

        if (selType === 'single') {
          return (
            <SelectInput
              source="correctAnswer"
              label="Correct Answer"
              fullWidth
              variant="standard"
              optionText="name"
              optionValue="id"
              choices={choices}
              validate={validateRequired('Correct Answer')}
            />
          )
        }

        if (selType === 'multiple') {
          return (
            <SelectArrayInput
              source="correctAnswer"
              label="Correct Answers"
              fullWidth
              variant="standard"
              optionText="name"
              optionValue="id"
              choices={choices}
              validate={validateRequired('Correct Answers')}
            />
          )
        }

        if (selType === 'multiple_ordered') {
          return (
            <Box>
              <SelectArrayInput
                source="correctAnswer"
                label="Correct Answers (select then reorder)"
                fullWidth
                variant="standard"
                optionText="name"
                optionValue="id"
                choices={choices}
                validate={validateRequired('Correct Answers')}
              />
              <OrderedCorrectAnswerManager choices={choices} />
            </Box>
          )
        }

        return null
      }}
    </FormDataConsumer>
  )
}

const CourseQuizTab = () => {
  return (
    <Box sx={{ width: '100%' }}>
      <ArrayInput source="quizzes" fullWidth>
        <SimpleFormIterator getItemLabel={index => `Quiz ${index + 1}`}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormDataConsumer>
                {({ formData }) => {
                  if (!formData || !formData.episodes)
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No episodes available yet. Create episodes in Course
                        Details tab.
                      </Typography>
                    )

                  const episodeChoices = (formData.episodes || [])
                    .filter(ep => ep && ep.id)
                    .map(ep => ({ id: ep.id, title: ep.title }))

                  return (
                    <SelectInput
                      label="Episode"
                      source="episodeId"
                      fullWidth
                      variant="standard"
                      optionText="title"
                      optionValue="id"
                      choices={episodeChoices}
                      validate={validateRequired('Episode')}
                    />
                  )
                }}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={3}>
              <OrderInput />
            </Grid>
            <Grid item xs={12} md={3}>
              <SelectInput
                source="questionType"
                label="Question Type"
                fullWidth
                variant="standard"
                choices={[{ id: 'text', name: 'Text' }]}
                defaultValue="text"
                validate={validateRequired('Question Type')}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <SelectInput
                source="answerSelectionType"
                label="Answer Selection Type"
                fullWidth
                variant="standard"
                choices={[
                  { id: 'single', name: 'Single' },
                  { id: 'multiple', name: 'Multiple' },
                  { id: 'multiple_ordered', name: 'Multiple (Ordered)' },
                ]}
                validate={validateRequired('Answer Selection Type')}
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextInput
                source="question"
                label="Question"
                fullWidth
                variant="standard"
                validate={validateRequired('Question')}
                helperText="Với câu hỏi điền từ, nhập 3 dấu _ để đại diện cho từ cần điền. Ex: Tôi ___ bạn."
              />
            </Grid>

            <Grid item xs={12}>
              <ArrayInput source="answers" fullWidth>
                <SimpleFormIterator getItemLabel={idx => `Answer ${idx + 1}`}>
                  <AnswerInput />
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>

            <Grid item xs={12} md={12}>
              <CorrectAnswerInput />
            </Grid>

            <Grid item xs={12}>
              <TextInput
                source="explanation"
                label="Explanation"
                fullWidth
                variant="standard"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </SimpleFormIterator>
      </ArrayInput>
    </Box>
  )
}

export default CourseQuizTab
